/**
 * AssetMediaManager — drag-drop image + video upload, delete, and gallery preview
 * for any partner asset (cars/yachts/portfolio/services/property).
 *
 * Calls:
 *   POST   /api/partner/{asset_type}/{asset_id}/upload-media (multipart, media_type form field)
 *   DELETE /api/partner/{asset_type}/{asset_id}/media        (json body with media_type+url)
 *
 * Limits: 12MB image, 60MB video. Backend rejects unsupported extensions with 400.
 */
import { useState, useRef } from 'react';
import { ImagePlus, Film, X, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthHeaders } from '@/utils/auth';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const absoluteMediaUrl = (url) => (url?.startsWith('http') ? url : `${BACKEND}${url || ''}`);

const AssetMediaManager = ({ assetType, assetId, images = [], videos = [], onChange, compact = false }) => {
  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);
  const [uploading, setUploading] = useState(null); // 'image' | 'video' | null
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file, mediaType) => {
    if (!assetId) {
      toast.error('Save the asset first, then upload media.');
      return;
    }
    if (!file) return;
    setUploading(mediaType);
    try {
      const fd = new FormData();
      fd.append('media_type', mediaType);
      fd.append('file', file);
      const res = await axios.post(
        `${API}/partner/${assetType}/${assetId}/upload-media`,
        fd,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${mediaType === 'video' ? 'Video' : 'Image'} uploaded`);
      const next = mediaType === 'video'
        ? { images, videos: [...(videos || []), res.data.url] }
        : { images: [...(images || []), res.data.url], videos };
      onChange?.(next);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(null);
    }
  };

  const removeMedia = async (url, mediaType) => {
    try {
      await axios.delete(
        `${API}/partner/${assetType}/${assetId}/media`,
        { headers: getAuthHeaders(), data: { media_type: mediaType, url } }
      );
      const next = mediaType === 'video'
        ? { images, videos: (videos || []).filter(v => v !== url) }
        : { images: (images || []).filter(i => i !== url), videos };
      onChange?.(next);
      toast.success('Removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const onFileChosen = (e, mediaType) => {
    const file = e.target.files?.[0];
    if (file) upload(file, mediaType);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    upload(file, isVideo ? 'video' : 'image');
  };

  return (
    <div className="space-y-3" data-testid={`asset-media-manager-${assetType}`}>
      {/* Drop zone + upload buttons */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex flex-wrap items-center gap-3 p-4 rounded-lg border-2 border-dashed transition-colors ${
          dragOver ? 'border-gold bg-gold/5' : 'border-white/15 bg-white/[0.02]'
        }`}
      >
        <Upload className="w-5 h-5 text-text-secondary" />
        <span className="text-sm text-text-secondary">
          {assetId ? 'Drag and drop or' : 'Save asset first, then upload'}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!assetId || uploading === 'image'}
          onClick={() => imgInputRef.current?.click()}
          className="border-gold/30 text-gold hover:bg-gold/10"
          data-testid={`upload-image-btn-${assetType}`}
        >
          {uploading === 'image' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />}
          Image
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!assetId || uploading === 'video'}
          onClick={() => vidInputRef.current?.click()}
          className="border-gold/30 text-gold hover:bg-gold/10"
          data-testid={`upload-video-btn-${assetType}`}
        >
          {uploading === 'video' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Film className="w-4 h-4 mr-1" />}
          Video
        </Button>
        <span className="text-[11px] text-text-muted ml-auto">Image ≤ 12MB · Video ≤ 60MB</span>
        <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={(e) => onFileChosen(e, 'image')} />
        <input ref={vidInputRef} type="file" accept="video/*" hidden onChange={(e) => onFileChosen(e, 'video')} />
      </div>

      {/* Image gallery */}
      {(images?.length || 0) > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-text-muted mb-2">Images ({images.length})</p>
          <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'} gap-2`}>
            {images.map((url, i) => (
              <div key={url + i} className="relative group rounded overflow-hidden aspect-square bg-white/5">
                <img src={absoluteMediaUrl(url)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeMedia(url, 'image')}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-btn-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video gallery */}
      {(videos?.length || 0) > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-text-muted mb-2">Videos ({videos.length})</p>
          <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'} gap-2`}>
            {videos.map((url, i) => (
              <div key={url + i} className="relative group rounded overflow-hidden bg-white/5">
                <video src={absoluteMediaUrl(url)} controls className="w-full max-h-48 object-cover" />
                <button
                  type="button"
                  onClick={() => removeMedia(url, 'video')}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-video-btn-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetMediaManager;
